import { AttendanceSheetResponseDto } from '@/api.types';
import { AttendanceSheetStatusEnum } from '../../../enums/attendance-sheet-status.enum';
import { NavigationView } from '@/shared/constants/navigation-view.enum';
import {
  validarDiasUteisParaAssinatura,
  type ValidarDiasUteisResult,
} from '@dpese-sgi/contracts';

export function validarDiasUteisFolhaParaAssinatura(
  folha: AttendanceSheetResponseDto,
): ValidarDiasUteisResult {
  const mm = String(folha.mes).padStart(2, '0');
  const diasNaFolha = new Map<number, (typeof folha.dias)[number]>();
  for (const d of folha.dias ?? []) {
    diasNaFolha.set(d.dia, d);
  }

  const ultimoDia = new Date(folha.ano, folha.mes, 0).getDate();
  const dias = [];
  for (let dia = 1; dia <= ultimoDia; dia++) {
    const dd = String(dia).padStart(2, '0');
    const registro = diasNaFolha.get(dia);
    dias.push({
      dia,
      data: `${folha.ano}-${mm}-${dd}`,
      feriado: registro?.feriado ?? null,
      // Igual à API (`diaToStatus.has`): presença em folha.dias, não o valor de status.
      cadastrado: diasNaFolha.has(dia),
      status: registro?.status ?? null,
    });
  }

  return validarDiasUteisParaAssinatura({
    diasLivres: !!folha.estagio?.lotacao?.diaslivres,
    fimSemana: !!folha.estagio?.fim_semana,
    dias,
  });
}

/** Minutos entre entrada e saída da lotação; fallback 300 se dados incompletos. */
export function calcularCargaHorariaMaximaFromLotacao(lotacao: {
  entrada?: string;
  saida?: string;
} | null | undefined): number {
  if (!lotacao?.entrada || !lotacao?.saida) {
    return 300;
  }

  const [entradaH, entradaM] = lotacao.entrada.split(':').map(Number);
  const [saidaH, saidaM] = lotacao.saida.split(':').map(Number);

  const entradaMin = entradaH * 60 + entradaM;
  const saidaMin = saidaH * 60 + saidaM;

  return saidaMin - entradaMin;
}

export function obterUltimaJustificativaRejeicao(folha: AttendanceSheetResponseDto): string {
  const assinaturasComJustificativa = folha?.assinaturas
    ?.filter((a) => a.justificativa)
    ?.sort((a, b) => new Date(b.criadoEm).getTime() - new Date(a.criadoEm).getTime());
  return assinaturasComJustificativa?.[0]?.justificativa ?? 'Não informado';
}

export function cpfSoDigitos(cpf: string | null | undefined): string {
  return String(cpf ?? '').replace(/\D/g, '');
}

/**
 * Compara CPFs só por dígitos (aceita máscara).
 * Exige ao menos um dígito em ambos para não tratar vazio === vazio como match.
 */
export function cpfsIguais(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const da = cpfSoDigitos(a);
  const db = cpfSoDigitos(b);
  return da.length > 0 && da === db;
}

/** Preferência: CPF da sessão com 11+ dígitos; senão JWT; senão o que houver na sessão. */
export function resolveCpfSessao(
  cpfEstado: string | null | undefined,
  cpfDoJwt?: string | null | undefined,
): string {
  const fromState = cpfSoDigitos(cpfEstado);
  const fromJwt = cpfSoDigitos(cpfDoJwt);
  if (fromState.length >= 11) return fromState;
  if (fromJwt.length >= 11) return fromJwt;
  return fromState || fromJwt;
}

type ResolveActionLabelsInput = {
  folha: AttendanceSheetResponseDto | undefined;
  cpfSessao: string | null | undefined;
  podeAtualizarFolha: boolean;
  authVisao: NavigationView | null;
};

/**
 * Rótulos dos botões de ação conforme status da folha e papel da sessão.
 * Derivado de forma síncrona (useMemo) para evitar delay/vazio no primeiro paint.
 */
export function resolveAttendanceSheetActionLabels({
  folha,
  cpfSessao,
  podeAtualizarFolha,
  authVisao,
}: ResolveActionLabelsInput): { textoBtnA: string; textoBtnB: string } {
  if (!folha) return { textoBtnA: '', textoBtnB: '' };

  const ehSubcorregedoria = authVisao === NavigationView.Subcorregedoria;
  const ehResponsavelPorVisao =
    authVisao === NavigationView.Defensor ||
    authVisao === NavigationView.DefensorDiretor ||
    authVisao === NavigationView.Colaborador;

  switch (folha.status) {
    case AttendanceSheetStatusEnum.CONCLUIDA:
      return { textoBtnA: '', textoBtnB: '' };

    case AttendanceSheetStatusEnum.EM_PREENCHIMENTO:
    case AttendanceSheetStatusEnum.EM_CORRECAO:
      if (cpfsIguais(cpfSessao, folha.estagiario?.cpf)) {
        return { textoBtnA: 'Enviar para Assinatura', textoBtnB: '' };
      }
      if (ehSubcorregedoria && podeAtualizarFolha) {
        return { textoBtnA: 'Enviar para Assinatura.', textoBtnB: '' };
      }
      return { textoBtnA: '', textoBtnB: '' };

    case AttendanceSheetStatusEnum.AGUARDANDO_ASSINATURA:
      if (
        ehResponsavelPorVisao &&
        podeAtualizarFolha &&
        cpfsIguais(cpfSessao, folha.responsavel?.cpf)
      ) {
        return {
          textoBtnA: 'Assinar e Enviar para a Subdefensoria',
          textoBtnB: 'Pedir Correção',
        };
      }
      if (ehSubcorregedoria && podeAtualizarFolha) {
        return {
          textoBtnA: 'Assinar e Enviar para a Subdefensoria.',
          textoBtnB: 'Pedir Correção.',
        };
      }
      return { textoBtnA: '', textoBtnB: '' };

    case AttendanceSheetStatusEnum.AGUARDANDO_REVISAO:
      if (ehSubcorregedoria && podeAtualizarFolha) {
        return { textoBtnA: 'Aceitar e Finalizar', textoBtnB: 'Pedir Correção' };
      }
      return { textoBtnA: '', textoBtnB: '' };

    default:
      return { textoBtnA: '', textoBtnB: '' };
  }
}
